import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Layout } from "../components/Layout";
import { toast } from "sonner";
import {
  HardDrives, CloudArrowDown, CheckCircle, CircleNotch, Trash,
  ShieldCheck, WarningCircle, ArrowsClockwise, Package, LinkSimple,
} from "@phosphor-icons/react";

const KIND_COLOR = { pkg: "#00F0FF", dmg: "#B37FEB", zip: "#00FF41" };
const BUSY = new Set(["queued", "resolving", "downloading", "uploading"]);

const fmtSize = (b) => {
  if (!b) return "—";
  if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / 1024).toFixed(0)} KB`;
};
const fmtDate = (iso) => {
  if (!iso) return "never";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const STATUS_LABEL = {
  idle: "not hosted", queued: "queued…", resolving: "resolving…",
  downloading: "downloading…", uploading: "uploading…", done: "hosted",
  hosted: "hosted", error: "failed",
};

export default function PackageLibrary() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/packages");
      setRows(data.packages || []);
      const anyBusy = (data.packages || []).some((p) => BUSY.has(p.status));
      setSyncing(anyBusy);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (syncing) pollRef.current = setInterval(load, 2500);
    return () => clearInterval(pollRef.current);
  }, [syncing]);

  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/packages/sync", {});
      toast.success(`Syncing ${data.started.length} package(s)…`);
      load();
    } catch (err) {
      setSyncing(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sync failed to start");
    }
  };

  const syncOne = async (id) => {
    try {
      await api.post("/admin/packages/sync", { app_ids: [id] });
      setSyncing(true);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sync failed to start");
    }
  };

  const unhost = async (id, name) => {
    if (!window.confirm(`Remove hosted copy of ${name}? It will fall back to live vendor resolve.`)) return;
    try {
      await api.delete(`/admin/packages/${id}`);
      toast.success(`${name} removed from hosted library`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    }
  };

  const hostable = rows.filter((r) => r.hostable);
  const hostedCount = rows.filter((r) => r.hosted).length;
  const totalSize = rows.reduce((a, r) => a + (r.size || 0), 0);

  const StatusBadge = ({ r }) => {
    const busy = BUSY.has(r.status);
    const done = r.status === "done" || r.status === "hosted" || (r.hosted && r.status === "idle");
    const err = r.status === "error";
    let color = "#71717A", Icon = Package;
    if (busy) { color = "#00F0FF"; Icon = CircleNotch; }
    else if (err) { color = "#FF5F56"; Icon = WarningCircle; }
    else if (done) { color = "#00FF41"; Icon = CheckCircle; }
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px]" style={{ color }} data-testid={`pkg-status-${r.id}`}>
        <Icon size={13} weight={done ? "fill" : "regular"} className={busy ? "animate-spin" : ""} />
        {STATUS_LABEL[done ? "hosted" : r.status] || r.status}
      </span>
    );
  };

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6">
        <h1 className="text-2xl tracking-tight flex items-center gap-2">
          <HardDrives size={22} weight="fill" className="text-[#00F0FF]" /> Package Library
          <span className="ml-2 font-mono text-[9px] uppercase tracking-wider border border-[#B37FEB] text-[#C79BF0] px-2 py-0.5">self-hosted</span>
        </h1>
        <p className="text-sm text-[#A1A1AA] font-mono mt-1">
          Host catalog binaries on your own storage for absolute plug-and-play reliability. Hosted apps are served from MDM&nbsp;Forge instead of resolving live from GitHub/vendors at install time.
        </p>
      </div>

      <div className="p-8 space-y-6">
        {/* Stats + actions */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="border border-[#27272A] bg-[#0f0f11] px-4 py-3" data-testid="pkg-stat-hosted">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#71717A]">Hosted</div>
            <div className="text-xl text-[#00FF41] font-semibold">{hostedCount}<span className="text-sm text-[#52525B]"> / {hostable.length} hostable</span></div>
          </div>
          <div className="border border-[#27272A] bg-[#0f0f11] px-4 py-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#71717A]">Total size</div>
            <div className="text-xl text-[#FAFAFA] font-semibold">{fmtSize(totalSize)}</div>
          </div>
          <button data-testid="pkg-sync-all-btn" onClick={syncAll} disabled={syncing}
            className="ml-auto flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-5 py-2.5 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50">
            {syncing ? <CircleNotch size={16} className="animate-spin" /> : <CloudArrowDown size={16} weight="fill" />}
            {syncing ? "Syncing…" : "Sync all hostable"}
          </button>
        </div>

        <p className="font-mono text-[11px] text-[#52525B] flex items-center gap-1.5">
          <ShieldCheck size={13} className="text-[#B37FEB]" />
          Proprietary vendor apps (Chrome, Zoom, Slack, 1Password, etc.) stay live-resolve to respect their redistribution terms. Only open-source / free-redistribution apps are hostable.
        </p>

        {/* Table */}
        <div className="border border-[#27272A]" data-testid="pkg-table">
          <div className="grid grid-cols-[1.4fr_0.5fr_0.7fr_0.8fr_1fr_1fr_0.9fr] gap-2 px-4 py-3 border-b border-[#27272A] bg-[#121214] font-mono text-[10px] uppercase tracking-wider text-[#71717A]">
            <div>App</div><div>Kind</div><div>Team ID</div><div>Version</div><div>Size / Synced</div><div>Status</div><div className="text-right">Actions</div>
          </div>
          {loading && <div className="px-4 py-8 text-center font-mono text-sm text-[#71717A]">Loading…</div>}
          {!loading && rows.map((r) => (
            <div key={r.id} data-testid={`pkg-row-${r.id}`}
              className="grid grid-cols-[1.4fr_0.5fr_0.7fr_0.8fr_1fr_1fr_0.9fr] gap-2 px-4 py-3 border-b border-[#1c1c1f] items-center text-sm hover:bg-[#0f0f11]">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: KIND_COLOR[r.kind] || "#71717A" }} />
                <span className="truncate text-[#FAFAFA]">{r.name}</span>
                {!r.hostable && <span className="font-mono text-[8px] uppercase border border-[#3f3f46] text-[#71717A] px-1 py-0.5 shrink-0">live only</span>}
              </div>
              <div className="font-mono text-[10px] uppercase" style={{ color: KIND_COLOR[r.kind] || "#71717A" }}>{r.kind}</div>
              <div className="font-mono text-[10px] text-[#A1A1AA] truncate">{r.team_id || "—"}</div>
              <div className="font-mono text-[10px] text-[#00FF41] truncate">{r.version || "—"}</div>
              <div className="font-mono text-[10px] text-[#71717A]">
                <div>{fmtSize(r.size)}</div>
                <div className="text-[#52525B]">{r.hosted ? fmtDate(r.synced_at) : ""}</div>
              </div>
              <div>
                <StatusBadge r={r} />
                {r.status === "error" && r.error && (
                  <div className="font-mono text-[9px] text-[#FF5F56] truncate max-w-[160px]" title={r.error}>{r.error}</div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                {r.hostable ? (
                  <>
                    <button data-testid={`pkg-sync-${r.id}`} onClick={() => syncOne(r.id)} disabled={BUSY.has(r.status)}
                      title={r.hosted ? "Re-sync latest" : "Host this app"}
                      className="text-[#71717A] hover:text-[#00F0FF] disabled:opacity-40">
                      {r.hosted ? <ArrowsClockwise size={15} /> : <CloudArrowDown size={15} />}
                    </button>
                    {r.hosted && r.public_url && (
                      <a data-testid={`pkg-link-${r.id}`} href={r.public_url} target="_blank" rel="noreferrer"
                        title="Open hosted URL" className="text-[#71717A] hover:text-[#B37FEB]"><LinkSimple size={15} /></a>
                    )}
                    {r.hosted && (
                      <button data-testid={`pkg-unhost-${r.id}`} onClick={() => unhost(r.id, r.name)}
                        title="Remove hosted copy" className="text-[#71717A] hover:text-[#FF5F56]"><Trash size={15} /></button>
                    )}
                  </>
                ) : (
                  <span className="font-mono text-[9px] text-[#52525B]">—</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
